/**
 * AMAÇ: Girişsiz ziyaretçilerin karşılandığı herkese açık ana sayfa (SEO için statik içerik).
 * Dil, URL'den gelir (/, /en, /de, /fr, /es, /pt, /ru) — her biri kendi meta etiketleri ve
 * hreflang alternatifleriyle Google'a ayrı bir dil sürümü olarak sunulur.
 * Oturumu olan kullanıcı otomatik /dashboard'a yönlenir.
 */
import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Camera, Flame, Award, Snowflake, BarChart3, Target } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useI18n, translate, LANGUAGES } from '../context/I18nContext';
import Logo from '../components/Logo';
import { setMetaTag, setCanonical } from '../utils/seoMeta';

const SITE_URL = 'https://surekly.com';
const pathFor = (code) => (code === 'tr' ? '/' : `/${code}`);

const FEATURES = [
  { key: 'photo',   icon: Camera },
  { key: 'streak',  icon: Flame },
  { key: 'badges',  icon: Award },
  { key: 'freeze',  icon: Snowflake },
  { key: 'stats',   icon: BarChart3 },
  { key: 'goal',    icon: Target },
];

const STEP_KEYS = ['step1', 'step2', 'step3'];

export default function LandingPage({ lang = 'tr' }) {
  const { user, loading } = useAuth();
  const { changeLang, localeVersion } = useI18n();
  const navigate = useNavigate();
  const tt = (key) => translate(lang, key);

  // Bu dille gelen ziyaretçi kayıt/giriş'e geçtiğinde de aynı dilde devam etsin
  useEffect(() => { changeLang(lang); }, [lang, changeLang]);

  useEffect(() => {
    if (!loading && user) navigate('/dashboard', { replace: true });
  }, [loading, user, navigate]);

  useEffect(() => {
    const title = tt('landing.meta.title');
    const desc  = tt('landing.meta.description');
    const url   = `${SITE_URL}${pathFor(lang)}`;

    document.documentElement.lang = lang;
    document.title = title;
    setMetaTag('name', 'description', desc);
    setMetaTag('property', 'og:title', title);
    setMetaTag('property', 'og:description', desc);
    setMetaTag('property', 'og:url', url);
    setMetaTag('property', 'og:locale', lang);
    setMetaTag('name', 'twitter:title', title);
    setMetaTag('name', 'twitter:description', desc);
    setCanonical(url);

    // hreflang alternatifleri — her dil sürümü diğerlerine işaret eder.
    // Statik build kabuğu (bkz. scripts/generate-lang-shells.js) zaten bu dilin
    // hreflang'larını gömüyor — önce onları temizleyip tekrar eklemiyoruz, yinelenmesinler.
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
    const added = [];
    const addAlt = (hreflang, href) => {
      const el = document.createElement('link');
      el.rel = 'alternate';
      el.hreflang = hreflang;
      el.href = href;
      document.head.appendChild(el);
      added.push(el);
    };
    LANGUAGES.forEach(({ code }) => addAlt(code, `${SITE_URL}${pathFor(code)}`));
    addAlt('x-default', `${SITE_URL}/`);

    // SSS bölümüyle birebir aynı içerik — Google'ın FAQ zengin sonucu göstermesi için
    const faqItems = translate(lang, 'landing.faq.items');
    if (Array.isArray(faqItems)) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqItems.map(({ q, a }) => ({
          '@type': 'Question',
          name: q,
          acceptedAnswer: { '@type': 'Answer', text: a },
        })),
      });
      document.head.appendChild(script);
      added.push(script);
    }

    return () => added.forEach((el) => el.remove());
  }, [lang, localeVersion]);

  if (loading || user) return null;

  return (
    <div className="min-h-screen bg-surface-page dark:bg-dark-page transition-colors duration-200">
      {/* Üst bar */}
      <header className="max-w-5xl mx-auto px-4 sm:px-6 py-5 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <Logo size={36} />
          <span className="text-lg font-bold text-slate-800 dark:text-slate-100">Surekly</span>
        </div>
        <Link to="/login" className="btn-secondary">{tt('landing.nav.login')}</Link>
      </header>

      {/* Hero */}
      <main>
        <section className="max-w-5xl mx-auto px-4 sm:px-6 pt-10 pb-16 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-800 dark:text-slate-100 tracking-tight">
            {tt('landing.hero.titleLine1')} <span className="text-primary-500">{tt('landing.hero.titleHighlight')}</span>
          </h1>
          <p className="mt-5 text-lg text-ink-soft max-w-2xl mx-auto">
            {tt('landing.hero.subtitle')}
          </p>
          <div className="mt-8 flex items-center justify-center gap-3">
            <Link to="/login" className="btn-primary btn-lg">{tt('landing.hero.ctaPrimary')}</Link>
            <Link to="/login" className="btn-secondary btn-lg">{tt('landing.hero.ctaSecondary')}</Link>
          </div>
        </section>

        {/* Özellikler */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-16">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 text-center mb-8">
            {tt('landing.features.title')}
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURES.map(({ key, icon: Icon }) => (
              <div key={key} className="card">
                <div className="w-10 h-10 rounded-md bg-primary-50 dark:bg-dark-hover flex items-center justify-center mb-3">
                  <Icon size={20} className="text-primary-500" />
                </div>
                <h3 className="font-semibold text-slate-800 dark:text-slate-100">{tt(`landing.features.${key}.title`)}</h3>
                <p className="text-sm text-ink-soft mt-1">{tt(`landing.features.${key}.desc`)}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Nasıl çalışır */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-16">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 text-center mb-8">
            {tt('landing.steps.title')}
          </h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {STEP_KEYS.map((key, i) => (
              <div key={key} className="text-center">
                <div className="w-9 h-9 rounded-full bg-primary-500 text-white font-bold flex items-center justify-center mx-auto mb-3">
                  {i + 1}
                </div>
                <h3 className="font-semibold text-slate-800 dark:text-slate-100">{tt(`landing.steps.${key}.title`)}</h3>
                <p className="text-sm text-ink-soft mt-1">{tt(`landing.steps.${key}.desc`)}</p>
              </div>
            ))}
          </div>
        </section>

        {/* SSS */}
        <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-16">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 text-center mb-8">
            {tt('landing.faq.title')}
          </h2>
          <div className="space-y-3">
            {tt('landing.faq.items').map(({ q, a }) => (
              <details key={q} className="card group">
                <summary className="font-semibold text-slate-800 dark:text-slate-100 cursor-pointer list-none flex items-center justify-between gap-4">
                  {q}
                  <span className="text-ink-soft transition-transform group-open:rotate-45 shrink-0">+</span>
                </summary>
                <p className="text-sm text-ink-soft mt-3">{a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Kapanış CTA */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-20 text-center">
          <div className="card py-10 px-6">
            <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100">
              {tt('landing.closing.title')}
            </h2>
            <p className="text-ink-soft mt-2">
              {tt('landing.closing.subtitle')}
            </p>
            <Link to="/login" className="btn-primary btn-lg mt-6 inline-flex">{tt('landing.closing.cta')}</Link>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="max-w-5xl mx-auto px-4 sm:px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-ink-soft border-t border-slate-100 dark:border-dark-border">
        <span>© {new Date().getFullYear()} Surekly</span>
        <div className="flex items-center gap-4">
          <Link to={lang === 'tr' ? '/blog' : `/${lang}/blog`} className="hover:text-ink">Blog</Link>
          <Link to="/gizlilik" className="hover:text-ink">{tt('landing.footer.privacy')}</Link>
          <Link to="/kosullar" className="hover:text-ink">{tt('landing.footer.terms')}</Link>
        </div>
      </footer>
    </div>
  );
}
